import React, { JSX } from 'react';
import { ControllerRenderProps, FieldErrors, FieldPath, FieldValues } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import styles from 'shared/components/g2-external-id-text-field/g2-external-id-text-field.module.scss';
import useG2ExternalIdTextField from 'shared/components/g2-external-id-text-field/hooks';

type Props<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  errors: FieldErrors<TFieldValues>;
  isImported?: boolean;
  label?: string;
};

const G2ExternalIdTextField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>,
): React.JSX.Element => {
  const { formatExternalId } = useG2ExternalIdTextField();
  const G2_TRANSMITTER_PLACEHOLDER = 'XX-XX-XX';

  const renderPlaceholderStyle = (
    field: ControllerRenderProps<TFieldValues, TName>,
  ): {
    input: {
      disabled: boolean | undefined;
      startAdornment: JSX.Element;
    };
  } => ({
    input: {
      disabled: props.isImported,
      startAdornment: (
        <span className={styles.placeHolderStyle}>
          <span className={styles.placeholderOpacity}>{field.value ?? ''}</span>
          <span>{G2_TRANSMITTER_PLACEHOLDER.slice((field.value ?? '').length)}</span>
        </span>
      ),
    },
  });

  return (
    <TextField
      {...props.field}
      onChange={(e) => props.field.onChange(formatExternalId(e.target.value))}
      slotProps={renderPlaceholderStyle(props.field)}
      variant="outlined"
      id="externalIdInput"
      label={props.label}
      error={!!props.errors.externalId}
      helperText={typeof props.errors.externalId?.message === 'string' ? props.errors.externalId.message : undefined}
      className="form-field"
    />
  );
};

export default G2ExternalIdTextField;
